import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, Slider, Segmented, Button, Tooltip } from 'antd';
import { MdRotateLeft, MdRotateRight, MdZoomIn, MdZoomOut } from 'react-icons/md';
import { getCroppedFile } from './cropImage';
import styles from './styles.module.css';

// Any ratio is shown whole in the app (contain-fit over a blurred backdrop);
// square fills the hero card best on most phones.
const ASPECTS = [
    { label: 'Square (best fit)', value: 1 },
    { label: '4:3', value: 4 / 3 },
    { label: '3:4', value: 3 / 4 },
    { label: '16:9', value: 16 / 9 },
];

/**
 * Crop / zoom / rotate dialog for still images (GIFs skip it to keep their animation).
 * `source` is { src: dataURL, name } or null when closed.
 * Calls onDone(File) with the cropped, resized and compressed JPEG.
 */
const ImageCropModal = ({ source, onCancel, onDone }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [aspect, setAspect] = useState(1);
    const [cropPixels, setCropPixels] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Reset controls for every new image
    useEffect(() => {
        if (!source) return;
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setAspect(1);
        setCropPixels(null);
    }, [source]);

    const onCropComplete = useCallback((_, pixels) => setCropPixels(pixels), []);

    const handleOk = async () => {
        if (!cropPixels) return;
        setIsProcessing(true);
        try {
            const file = await getCroppedFile(source.src, cropPixels, rotation, source.name);
            onDone(file);
        } finally {
            setIsProcessing(false);
        }
    };

    const rotateBy = (deg) => setRotation((r) => (r + deg + 360) % 360);

    return (
        <Modal
            title="Crop image"
            open={!!source}
            onCancel={onCancel}
            onOk={handleOk}
            okText="Crop & upload"
            confirmLoading={isProcessing}
            width={640}
            destroyOnClose
            zIndex={1100}
        >
            <Segmented
                block
                options={ASPECTS}
                value={aspect}
                onChange={setAspect}
                style={{ marginBottom: 12 }}
            />

            <div className={styles['crop-area']}>
                {source && (
                    <Cropper
                        image={source.src}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspect}
                        minZoom={1}
                        maxZoom={4}
                        zoomSpeed={0.2}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                    />
                )}
            </div>

            <div className={styles['crop-controls']}>
                <MdZoomOut size={18} />
                <Slider
                    min={1}
                    max={4}
                    step={0.01}
                    value={zoom}
                    onChange={setZoom}
                    tooltip={{ formatter: (v) => `${v.toFixed(1)}×` }}
                    style={{ flex: 1 }}
                />
                <MdZoomIn size={18} />
                <Tooltip title="Rotate left">
                    <Button icon={<MdRotateLeft size={18} />} onClick={() => rotateBy(-90)} />
                </Tooltip>
                <Tooltip title="Rotate right">
                    <Button icon={<MdRotateRight size={18} />} onClick={() => rotateBy(90)} />
                </Tooltip>
            </div>
            <div className={styles['crop-hint']}>
                Drag to reposition · scroll or use the slider to zoom. The image is resized and
                compressed before upload.
            </div>
        </Modal>
    );
};

export default ImageCropModal;
